import React from "react";
import { Link } from "react-router-dom";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";

// Define socialLinks data
const socialLinks = [
  { url: "https://www.facebook.com/ppundergroundriver/", label: "Facebook", icon: "Facebook" },
  { url: "https://twitter.com", label: "Twitter", icon: "Twitter" },
  { url: "https://instagram.com", label: "Instagram", icon: "Instagram" },
  { url: "https://youtube.com", label: "YouTube", icon: "Youtube" }
];

// Define footerLinks data
const footerLinks = {
  explore: [
    { path: "/park", label: "Park Overview" },
    { path: "/wildlife", label: "Wildlife" },
    { path: "/cave-system", label: "Cave System" },
    { path: "/conservation", label: "Conservation" }
  ],
  visit: [
    { path: "/plan-visit", label: "Plan Your Visit" },
    { path: "/tours", label: "Tours & Activities" },
    { path: "/permits", label: "Permits" },
    { path: "/facilities", label: "Facilities" }
  ],
  research: [
    { path: "/studies", label: "Research Studies" },
    { path: "/publications", label: "Publications" },
    { path: "/partnerships", label: "Partnerships" },
    { path: "/data", label: "Data & Reports" }
  ],
  community: [
    { path: "/volunteer", label: "Volunteer" },
    { path: "/donate", label: "Support Us" },
    { path: "/events", label: "Events" },
    { path: "/contact", label: "Contact" }
  ]
};

export default function FooterSection() {
  return (
    <footer className="bg-gray-100 dark:bg-[#1a1a1a] text-gray-900 dark:text-white">
      {/* Newsletter Section */}
      <div className="border-b border-gray-300 dark:border-gray-800">
        <div className="container mx-auto px-4 lg:px-8 py-12">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Stay Connected with Conservation
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Receive monthly updates on conservation successes, research discoveries, and exclusive visitor opportunities
            </p>
            <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <Input
                type="email"
                placeholder="Enter your email"
                className="flex-1"
              />
              <Button
                variant="default"
                iconName="Send"
                iconPosition="right"
              >
                Subscribe
              </Button>
            </div>
          </div>
        </div>
      </div>
      {/* Main Footer Content */}
      <div className="container mx-auto px-4 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8 mb-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link to="/homepage" className="flex items-center space-x-3 mb-4">
              <img 
                src="/assets/images/Untitled-1765254849902.jpeg" 
                alt="Circular park logo with white border containing Puerto Princesa Subterranean River National Park text around the perimeter, established 1971 at bottom with yellow stars, center features underground river cave image with tourist boats" 
                className="h-12 w-12 object-contain bg-transparent dark:brightness-110"
              />
              <div>
                <div className="text-xl font-bold text-gray-900 dark:text-white">
                  PPSRNP
                </div>
                <div className="text-xs text-gray-700 dark:text-gray-200">
                  Digital Platform
                </div>
              </div>
            </Link>
            <p className="text-sm text-gray-800 dark:text-gray-200 mb-4">
              Protecting Earth's underground wonder through innovative conservation technology and community engagement.
            </p>
            <div className="flex space-x-3">
              {socialLinks?.map((social, idx) => (
                <a
                  key={idx}
                  href={social?.url}
                  className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-800 hover:bg-[var(--color-primary)] transition-colors flex items-center justify-center"
                  aria-label={social?.label}
                >
                  <Icon name={social?.icon} size={18} className="text-gray-900 dark:text-white" />
                </a>
              ))}
            </div>
          </div>

          {/* Explore Links */}
          <div>
            <h4 className="text-gray-900 dark:text-white font-semibold mb-4">
              Explore
            </h4>
            <ul className="space-y-2">
              {footerLinks?.explore?.map((link, idx) => (
                <li key={idx}>
                  <Link
                    to={link?.path}
                    className="text-sm text-gray-800 dark:text-gray-100 hover:text-[var(--color-primary)] transition-colors"
                  >
                    {link?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Visit Links */}
          <div>
            <h4 className="text-gray-900 dark:text-white font-semibold mb-4">
              Visit
            </h4>
            <ul className="space-y-2">
              {footerLinks?.visit?.map((link, idx) => (
                <li key={idx}>
                  <Link
                    to={link?.path}
                    className="text-sm text-gray-800 dark:text-gray-100 hover:text-[var(--color-primary)] transition-colors"
                  >
                    {link?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Research Links */}
          <div>
            <h4 className="text-gray-900 dark:text-white font-semibold mb-4">
              Research
            </h4>
            <ul className="space-y-2">
              {footerLinks?.research?.map((link, idx) => (
                <li key={idx}>
                  <Link
                    to={link?.path}
                    className="text-sm text-gray-800 dark:text-gray-100 hover:text-[var(--color-primary)] transition-colors"
                  >
                    {link?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Community Links */}
          <div>
            <h4 className="text-gray-900 dark:text-white font-semibold mb-4">
              Community
            </h4>
            <ul className="space-y-2">
              {footerLinks?.community?.map((link, idx) => (
                <li key={idx}>
                  <Link
                    to={link?.path}
                    className="text-sm text-gray-800 dark:text-gray-100 hover:text-[var(--color-primary)] transition-colors"
                  >
                    {link?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Contact Info */}
        <div className="border-t border-gray-300 dark:border-gray-800 pt-8 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-start space-x-3">
              <Icon name="MapPin" size={20} color="var(--color-primary)" />
              <div>
                <div className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                  Location
                </div>
                <div className="text-sm text-gray-800 dark:text-gray-100">
                  <div>Sabang, Puerto Princesa City</div>
                  <div>Palawan, Philippines 5300</div>
                </div>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Icon name="Phone" size={20} color="var(--color-primary)" />
              <div>
                <div className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                  Contact
                </div>
                <div className="text-sm text-gray-800 dark:text-gray-100">
                  <div>+63 48 434 2509</div>
                  <div>info@ppsrnp.gov.ph</div>
                </div>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Icon name="Clock" size={20} color="var(--color-primary)" />
              <div>
                <div className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                  Operating Hours
                </div>
                <div className="text-sm text-gray-800 dark:text-gray-100">
                  <div>Daily: 8:00 AM - 4:00 PM</div>
                  <div>Closed during typhoons</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Government & International Partners Section */}
        <div className="border-t border-gray-300 dark:border-gray-800 pt-8 mt-8">
          <div className="flex flex-col items-center space-y-4">
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
              Official Partners & Recognition
            </h4>
            <div className="flex flex-wrap items-center justify-center gap-8">
              {/* DENR Logo */}
              <div className="flex flex-col items-center">
                <img 
                  src="/assets/images/Untitled-1765255070000.jpeg" 
                  alt="Circular DENR (Department of Environment and Natural Resources) logo with green border, blue sky background, white tree symbol at top, green mountain/wave landscape at bottom, white text around perimeter, established 1987" 
                  className="h-20 w-20 object-contain"
                />
                <span className="text-xs text-gray-800 dark:text-gray-100 mt-2">
                  DENR Philippines
                </span>
              </div>

              {/* UNESCO Logo */}
              <div className="flex flex-col items-center">
                <img 
                  src="/assets/images/image-1765250296643.png" 
                  alt="Official UNESCO (United Nations Educational, Scientific and Cultural Organization) logo with temple icon and multilingual text" 
                  className="h-20 w-auto object-contain invert dark:invert-0"
                />
                <span className="text-xs text-gray-800 dark:text-gray-100 mt-2">
                  UNESCO World Heritage
                </span>
              </div>
            </div>
            
            {/* Copyright and Links Row - Moved here under logos */}
            <div className="w-full border-t border-gray-300 dark:border-gray-800 pt-6 mt-6">
              <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
                <div className="text-sm text-gray-900 dark:text-white">
                  © 2025 Puerto Princesa Subterranean River National Park. All rights reserved.
                </div>
                <div className="flex items-center space-x-6">
                  <Link
                    to="#"
                    className="text-sm text-gray-700 dark:text-gray-200 hover:text-[var(--color-primary)] transition-colors"
                  >
                    Privacy Policy
                  </Link>
                  <Link
                    to="#"
                    className="text-sm text-gray-700 dark:text-gray-200 hover:text-[var(--color-primary)] transition-colors"
                  >
                    Terms of Service
                  </Link>
                  <Link
                    to="#"
                    className="text-sm text-gray-700 dark:text-gray-200 hover:text-[var(--color-primary)] transition-colors"
                  >
                    Accessibility
                  </Link>
                </div>
              </div>
            </div>

            {/* Version Number - Moved to very bottom */}
            <div className="w-full border-t border-gray-300 dark:border-gray-800 pt-4 mt-4 flex justify-center">
              <div className="text-xs text-gray-700 dark:text-gray-200">
                v2.13.0
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}